import { DeviceType } from "@/common/hooks/use-device";
import { Filter } from "@/components/filter/_types";
import { RowAction } from "@/components/ui/table-row-actions";
import {
  AccessorColumnDef,
  CellContext,
  ColumnDefTemplate,
  DisplayColumnDef,
  Header,
  Row,
  RowData,
  RowSelectionState,
  ReactTable,
  Updater,
} from "@tanstack/react-table";
import { AxiosError } from "axios";
import { ComponentType, ReactNode } from "react";
import { RowWithIdentifier } from "./_helpers/row-identifier";
import { DataTableFeatureSet } from "./data-table-features";
import { GroupDetails } from "./predefined/_components/base-predefined-table.types";

export type DataTableRow<TData extends RowWithIdentifier> = Row<DataTableFeatureSet, TData & RowData>;
export type DataTableInstance<TData extends RowData & RowWithIdentifier> = ReactTable<DataTableFeatureSet, TData>;
export type DataTableCellContext<TData extends RowData, TValue = unknown> = CellContext<
  DataTableFeatureSet,
  TData,
  TValue
>;
export type DataTableHeaderInstance<TData extends RowData, TValue = unknown> = Header<
  DataTableFeatureSet,
  TData,
  TValue
>;

export enum DataTableHeightMode {
  Viewport = "viewport",
  Content = "content",
  Custom = "custom",
}

export enum TableBackground {
  Background = "background",
  Card = "card",
  Popover = "popover",
  Sidebar = "sidebar",
}

export const tableBackgroundStyles: Record<TableBackground, string> = {
  [TableBackground.Background]: "bg-background/80",
  [TableBackground.Card]: "bg-card/80",
  [TableBackground.Popover]: "bg-popover/80",
  [TableBackground.Sidebar]: "bg-sidebar/80",
};

export type ImplementedDisplayColumnDef<TData extends RowData & RowWithIdentifier, TValue = unknown> = Pick<
  DisplayColumnDef<DataTableFeatureSet, TData, TValue>,
  "id" | "size" | "minSize" | "maxSize" | "header" | "cell" | "enableSorting"
> & {
  enableSorting: boolean;
  visibility?: DeviceType[];
  align?: "left" | "center" | "right";
  link?: string;
  linkFn?: (row: TData) => string;
  linkType?: "content" | "cell";
  exportPaths?: string[];
};

export type ImplementedAccessorColumnDef<TData extends RowData & RowWithIdentifier, TValue = unknown> = Pick<
  AccessorColumnDef<DataTableFeatureSet, TData, TValue>,
  "id" | "size" | "minSize" | "maxSize" | "header" | "cell"
> & {
  id: string;
  cell: ColumnDefTemplate<DataTableCellContext<TData, TValue>>;
  enableSorting: boolean;
  visibility?: DeviceType[];
  align?: "left" | "center" | "right";
  link?: string;
  linkFn?: (row: TData) => string;
  linkType?: "content" | "cell";
  exportPaths?: string[];
  accessorFn: (row: TData) => TValue;
  meta?: {
    headerClassName?: string;
    cellClassName?: string;
  };
};

export type ImplementedColumnDef<TData extends RowData & RowWithIdentifier, TValue = unknown> =
  | ImplementedDisplayColumnDef<TData, TValue>
  | ImplementedAccessorColumnDef<TData, TValue>;

export interface ServerPaginationState {
  page: number;
  pageSize: number;
}

export interface TableFeatures {
  expansion?: boolean;
  hideExpansionButton?: boolean;
  multiSelection?: boolean;
  pagination?: boolean;
  search?: boolean;
}

export interface CustomSelectionAction<TData = unknown> {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  action: (selection: string[] | TData[]) => Promise<void>;
}

export interface TableActions<T extends RowData & RowWithIdentifier> {
  onClearSelection?: () => void;
  onFilterChange?: (filterId: string, values: string[]) => void;
  onPaginationChange?: (pagination: { page: number; pageSize: number }) => void;
  onRowClick?: (row: DataTableRow<T>) => void;
  onRowNavigate?: (row: DataTableRow<T>) => string | undefined;
  onSearchChange?: (searchValue: string) => void;
  onSelectAll?: () => void;
  onSelectionChange?: (selection: string[] | T[]) => void;
  onSortingChange?: (sorting: Array<{ field: string; direction: "asc" | "desc" }>) => void;
}

export enum FilterContainerCustomContentPosition {
  Top = "top",
  Bottom = "bottom",
}

export enum TimestampSortField {
  CreatedAt = "createdAt",
  UpdatedAt = "updatedAt",
  DeletedAt = "deletedAt",
}

export interface SortColumn<TSortId extends string = string> {
  id: TSortId;
  label: () => string;
}

export interface DataTableProps<
  TData extends RowData & RowWithIdentifier = RowData & RowWithIdentifier,
  TValue = unknown,
> {
  className?: string;
  columns: (ImplementedDisplayColumnDef<TData, TValue> | ImplementedAccessorColumnDef<TData, TValue>)[];
  customSelectionActions?: CustomSelectionAction<TData>[];
  data?: TData[];
  features?: TableFeatures;
  filters?: Filter<unknown, unknown>[];
  filterValues?: Record<string, string[]>;
  defaultFilterValues?: Record<string, string[]>;
  footerComponent?: ReactNode;
  headerComponent?: ReactNode;
  filterContainerClassName?: string;
  filterContainerCustomContent?: ReactNode;
  filterContainerCustomContentPosition?: FilterContainerCustomContentPosition;
  initialSorting?: Array<{ id: string; desc: boolean }>;
  isError?: AxiosError | boolean;
  isLoading?: boolean;
  meta?: Record<string, unknown>;
  paginationState?: ServerPaginationState;
  rowSelection?: RowSelectionState;
  searchPlaceholder?: string;
  SearchComponent?: React.ComponentType<{
    placeholder?: string;
    value?: string;
    onSearch?: (value: string) => void;
    onSearchChange?: (value: string) => void;
    className?: string;
  }>;
  showClearSelection?: boolean;
  showSelectionCount?: boolean;
  selection?: string[] | TData[];
  selectionMode?: "uuid" | "object";
  identifierProperty?: string;
  serverSidePagination?: boolean;
  sortingMapping?: Record<string, string[]>;
  sortColumns?: SortColumn[];
  timestampSortFields?: TimestampSortField[];
  subComponent?: ComponentType<{ row: DataTableRow<TData> }>;
  tableBackground?: TableBackground;
  tableClassName?: string;
  totalItems?: number;
  actions?: TableActions<TData>;
  rowActions?: (row: DataTableRow<TData>) => RowAction[];
  setRowSelection?: (updater: Updater<RowSelectionState>) => void;
  heightMode?: DataTableHeightMode;
  getRowCanExpand?: (row: TData) => boolean;
  getRowCanSelect?: (row: TData) => boolean;
  groupByCallback?: (rows: TData[]) => (GroupDetails<TData> | TData)[];
  searchValue: string;
  noDataTitle?: string;
  noDataDescription?: string;
  noDataIcon?: ComponentType<{ className?: string }>;
  noResultsTitle?: string;
  noResultsDescription?: string;
}
